import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Typography,
} from '@mui/material';
import React, { FormEvent, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  ICreateRelationshipPayload,
  useCreateRelationship,
} from '../../hooks/useUpdateRelationship';
import { IAsset } from '../../Interfaces/Asset/IAsset';
import { IConfig } from '../../Interfaces/IConfig';
import { IDocument } from '../../Interfaces/IDocument';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionRelationshipMember } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { getEmptyNaturalPerson } from '../../Interfaces/INaturalPerson';
import { useAlert } from '../core/AlertContext';
import { DialogResponsive } from '../core/DialogResponsive';
import {
  legalClientStore,
  useLegalClientStore,
} from '../public/Dialog/generic/legalClientStore';
import { NavRow } from '../public/Dialog/generic/NavRow';
import { present } from '../public/Dialog/generic/utilities';
import { EditLegalClient } from './EditLegalClient';
import { DataPrivacyAlert } from './SendLegalClient/DataPrivacyAlert';
import { LegalClientSummaryTable } from './SendLegalClient/FinalSummary';

function enrichMembersWithLegalClient(
  members: ILegalTransactionRelationshipMember[]
): ILegalTransactionRelationshipMember[] {
  const getLegalClient = legalClientStore.getState().getLegalClient;
  return members
    .map(member => {
      const legalClient = getLegalClient(member.idLegalClient);

      if (!legalClient) {
        return null;
      }

      return {
        ...member,
        LegalClient: legalClient,
      } as ILegalTransactionRelationshipMember;
    })
    .filter(present);
}

function enrichRelationshipsWithLegalClients(
  relationships: ILegalTransactionRelationship[]
) {
  return relationships.map(relationship => {
    return {
      ...relationship,
      Members: enrichMembersWithLegalClient(relationship.Members),
    };
  }) as ILegalTransactionRelationship[];
}

interface ISendLegalClientProps {
  configObject: IConfig;
  idLegalTransactionType: number;
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  assetArray?: IAsset[];
  documentArray?: IDocument[];
  handleNext: () => void;
  handleBack: () => void;
}

export const SendLegalClient: React.FC<ISendLegalClientProps> = ({
  configObject,
  idLegalTransactionType,
  relationshipArray,
  setRelationshipArray,
  assetArray,
  documentArray,
  handleNext,
  handleBack,
}) => {
  const [legalClientArray, putLegalClient] = useLegalClientStore(state => [
    state.legalClientArray,
    state.putLegalClient,
  ]);

  const legalClientMethods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: {
      ...getEmptyLegalClient(legalClientArray),
      ...getEmptyNaturalPerson(),
      data: {
        addToLegalTransaction: true,
      },
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const [isOpenAddNew, setIsOpenAddNew] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [isTermsAndConditionsChecked, setIsTermsAndConditionsChecked] =
    useState(false);

  const { createRelationship, isPendingRelationship } = useCreateRelationship();

  useEffect(() => {
    const legalClientsWithAccount = legalClientArray.filter(
      legalClient => legalClient.data.addToLegalTransaction
    );
    const allAccountsHaveEmail = legalClientsWithAccount.every(
      legalClient => z.string().email().safeParse(legalClient.Email).success
    );

    setSubmitDisabled(
      legalClientsWithAccount.length === 0 ||
        !allAccountsHaveEmail ||
        !isTermsAndConditionsChecked ||
        isPendingRelationship
    );
  }, [legalClientArray, isTermsAndConditionsChecked, isPendingRelationship]);

  const { showAlert } = useAlert();

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    const uploadData: ICreateRelationshipPayload = {
      LegalClientArray: legalClientArray.filter(
        legalClient => legalClient.data.addToLegalTransaction
      ),
      RelationshipArray: enrichRelationshipsWithLegalClients(relationshipArray),
      AssetArray: assetArray || [],
      DocumentArray: documentArray || [],
      idLegalTransactionType,
    };
    try {
      await createRelationship(uploadData);
      handleNext();
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Fehler beim Erstellen des Mandanten',
        severity: 'error',
      });
    }
  };

  const onAddNew = () => {
    legalClientMethods.reset({
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
      data: {
        addToLegalTransaction: true,
      },
    });
    setIsOpenAddNew(true);
  };

  const onSubmit = legalClientMethods.handleSubmit(data => {
    putLegalClient(data as ILegalClient);
    setIsOpenAddNew(false);
  });

  const { isValid } = legalClientMethods.formState;

  return (
    <>
      <DialogResponsive
        open={isOpenAddNew}
        onClose={() => setIsOpenAddNew(false)}
        title='Neue Person'
        maxWidth='lg'
      >
        <FormProvider {...legalClientMethods}>
          <form id='edit-legal-client-form' onSubmit={onSubmit} noValidate>
            <EditLegalClient disableCompanyLegalClient />
            <NavRow
              backButtonText='Abbrechen'
              nextButtonText='Übernehmen'
              justify='right'
              disableNext={!isValid}
            />
          </form>
        </FormProvider>
      </DialogResponsive>

      <Box sx={{ mt: 15 }} />
      <Typography variant='h6' sx={{ mb: 3 }}>
        Kontoinformationen
      </Typography>
      <Typography variant='caption'>
        Sie müssen mindestens ein Konto als <i>DiNo-Konto</i> markieren. Alle{' '}
        <i>DiNo-Konten</i> können sich im Mandantenportal anmelden und mit dem
        Notarbüro interagieren. Bitte beachten Sie, dass alle <i>DiNo-Konten</i>{' '}
        Zugriff auf den Vorgang haben, welcher aus dieser Datenerfassung
        entsteht. Um für einen Beteiligten ein <i>DiNo-Konto</i> anzulegen, muss
        eine E-Mail-Adresse hinterlegt werden. Existiert bereits ein{' '}
        <i>DiNo-Konto</i> mit der eingegebenen E-Mail-Adresse, wird der Vorgang
        dem vorhandenen <i>DiNo-Konto</i> zugeordnet.
      </Typography>
      <Collapse
        in={
          legalClientArray.filter(x => x.data.addToLegalTransaction).length ===
          0
        }
      >
        <Alert severity='warning'>
          Bitte markieren oder erstellen Sie ein <i>DiNo-Konto</i>.
        </Alert>
      </Collapse>

      <LegalClientSummaryTable
        relationshipArray={relationshipArray}
        setRelationshipArray={setRelationshipArray}
      />

      <Button
        sx={{ float: 'right', mt: 2 }}
        variant='contained'
        onClick={onAddNew}
      >
        Neues Konto anlegen
      </Button>

      <Box sx={{ mt: 10 }} />

      <DataPrivacyAlert
        configObject={configObject}
        isTermsAndConditionsChecked={isTermsAndConditionsChecked}
        setIsTermsAndConditionsChecked={setIsTermsAndConditionsChecked}
      />

      <Box sx={{ mt: 10 }} />

      <form onSubmit={handleSave} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button variant='outlined' onClick={handleBack} sx={{ mr: 1 }}>
            Zurück
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button variant='contained' disabled={submitDisabled} type='submit'>
            {isPendingRelationship ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Senden</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Senden'
            )}
          </Button>
        </Box>
      </form>
    </>
  );
};
